import React, { useState, useContext, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import Preloader from '../components/Preloader/Preloader';
import { ProductContext } from '../ProductProvider';
import { Helmet } from 'react-helmet-async';

const config = require('../config.json');

const contactUsModernStyles = `
  .home-contact-section {
    background: #ffffff;
    border-radius: 22px;
    border: 1px solid #e2e8f0;
    padding: 40px 36px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
    margin-bottom: 2rem;
  }

  .home-contact-header {
    margin-bottom: 28px;
  }

  .home-contact-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #e0f2fe;
    color: #0284c7;
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    padding: 4px 10px;
    border-radius: 12px;
    margin-bottom: 8px;
  }

  .home-contact-title {
    font-size: 2.1rem;
    font-weight: 800;
    color: #063547;
    line-height: 1.2;
    margin-bottom: 8px;
    letter-spacing: -0.4px;
  }

  .home-contact-subtitle {
    font-size: 1rem;
    color: #64748b;
    margin-bottom: 0;
  }

  /* Contact Information Cards */
  .contact-info-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
  }

  .contact-info-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 16px 20px;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    transition: all 0.2s ease;
  }

  .contact-info-card:hover {
    border-color: #cbd5e1;
    background: #f1f5f9;
  }

  .contact-info-icon-pill {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: #063547;
    color: #7ee8fa;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    flex-shrink: 0;
  }

  .contact-info-label {
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    margin-bottom: 2px;
  }

  .contact-info-value {
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }

  .contact-info-sub {
    font-size: 0.82rem;
    color: #64748b;
    margin-top: 2px;
  }

  /* Inquiry Form Container */
  .contact-form-card {
    background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    padding: 30px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);
  }

  .contact-form-title {
    font-size: 1.25rem;
    font-weight: 800;
    color: #063547;
    margin-bottom: 6px;
  }

  .contact-form-desc {
    font-size: 0.88rem;
    color: #64748b;
    margin-bottom: 20px;
  }

  .form-group-modern {
    margin-bottom: 16px;
  }

  .form-label-modern {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #475569;
    margin-bottom: 6px;
    display: block;
  }

  .form-input-modern,
  .form-select-modern,
  .form-textarea-modern {
    width: 100%;
    padding: 10px 14px;
    border-radius: 8px;
    border: 1.5px solid #cbd5e1;
    font-size: 0.94rem;
    color: #0f172a;
    background: #ffffff;
    outline: none;
    transition: all 0.18s ease;
  }

  .form-input-modern:focus,
  .form-select-modern:focus,
  .form-textarea-modern:focus {
    border-color: #0bb6d7;
    box-shadow: 0 0 0 3px rgba(11, 182, 215, 0.15);
  }

  .form-textarea-modern {
    min-height: 100px;
    resize: vertical;
  }

  .btn-contact-submit {
    background: linear-gradient(135deg, #063547 0%, #0a4b63 100%);
    color: #ffffff !important;
    border: none;
    border-radius: 10px;
    padding: 12px 26px;
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 12px rgba(6, 53, 71, 0.2);
    transition: all 0.2s ease;
    width: 100%;
    justify-content: center;
  }

  .btn-contact-submit:hover {
    background: linear-gradient(135deg, #0a4b63 0%, #0bb6d7 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(11, 182, 215, 0.35);
  }

  @media (max-width: 992px) {
    .home-contact-section {
      padding: 28px 20px;
    }
    .home-contact-title {
      font-size: 1.65rem;
    }
    .contact-form-card {
      padding: 22px 18px;
      margin-top: 24px;
    }
  }
`;

export default function ContactUs() {
  const { loggedUser, generateDate, generateAccessionNo } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();

  const [contactDetails, setContactDetails] = useState({
    username: '',
    emailID: '',
    phoneNumber: '',
    inquiryType: 'General Inquiry',
    message: ''
  });

  const [loaded, setLoaded] = useState(true);

  useEffect(() => {
    // Auto populate if user is logged in
    setContactDetails({
      username: loggedUser.name || '',
      emailID: loggedUser.email || '',
      phoneNumber: loggedUser.phone_number || '',
      inquiryType: 'General Inquiry',
      message: ''
    });
  }, [loggedUser]);

  const handleReset = () => {
    setContactDetails({
      username: loggedUser.name || '',
      emailID: loggedUser.email || '',
      phoneNumber: loggedUser.phone_number || '',
      inquiryType: 'General Inquiry',
      message: ''
    });
  };

  const validateFields = () => {
    const { username, emailID, phoneNumber, message } = contactDetails;
    if (!username.trim()) {
      enqueueSnackbar("Please enter your Name", { variant: 'warning' });
      return false;
    }
    if (!emailID.trim()) {
      enqueueSnackbar("Please enter your Email Address", { variant: 'warning' });
      return false;
    }
    if (!phoneNumber.trim()) {
      enqueueSnackbar("Please enter your Phone Number", { variant: 'warning' });
      return false;
    }
    if (!message.trim()) {
      enqueueSnackbar("Please write your inquiry or message", { variant: 'warning' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    try {
      setLoaded(false);
      const todayDate = typeof generateDate === 'function' ? await generateDate() : new Date().toISOString().slice(0, 10);
      const taccessionNo = typeof generateAccessionNo === 'function' ? await generateAccessionNo() : Math.floor(1000 + Math.random() * 9000);

      const params = {
        clientID: loggedUser.website || 'VJCollege',
        msgID: "MSG" + taccessionNo,
        username: loggedUser.nickname || contactDetails.username,
        emailID: contactDetails.emailID,
        phoneNumber: contactDetails.phoneNumber,
        inquiryType: contactDetails.inquiryType,
        message: contactDetails.message,
        dateSubmitted: todayDate,
        msgStatus: "New",
        dateClosed: "NA",
        comments: "NA",
        fullname: contactDetails.username,
        updatedBy: loggedUser.nickname || 'Guest',
        updatedOn: todayDate
      };

      await axios.post(`${config.api.invokeURL}/vjlibrary/contactus`, params);
      enqueueSnackbar(`Your inquiry was submitted successfully! (Ref ID: ${params.msgID})`, { variant: 'success' });
      handleReset();
    } catch (error) {
      console.error("Contact submit error:", error);
      enqueueSnackbar("Error submitting inquiry: " + (error.response?.data?.message || error.message), { variant: 'warning' });
    } finally {
      setLoaded(true);
    }
  };

  return (
    <div className="home-contact-section">
      <Helmet>
        <title>Contact Us & Library Help Desk | Vidyajyoti College of Theology</title>
        <meta
          name="description"
          content="Get in touch with the circulation and reference desk at Vidyajyoti College of Theology Library, Delhi."
        />
      </Helmet>
      <style>{contactUsModernStyles}</style>

      {!loaded && <Preloader show={true} />}

      <div className="home-contact-header">
        <div className="home-contact-badge">
          <i className="fa fa-headset"></i>
          <span>Student & Researcher Support</span>
        </div>
        <h2 className="home-contact-title">Circulation & Reference Help Desk</h2>
        <p className="home-contact-subtitle">
          Have questions regarding library memberships, book reservations, research journals, or inter-library loans? We are here to assist you.
        </p>
      </div>

      <div className="row g-4">
        {/* Left Column: Library Hours & Contact Cards */}
        <div className="col-lg-5">
          <div className="contact-info-list">
            {/* Library Timings Card */}
            <div className="contact-info-card">
              <div className="contact-info-icon-pill">
                <i className="fa fa-clock"></i>
              </div>
              <div>
                <div className="contact-info-label">Library & Reading Room Hours</div>
                <div className="contact-info-value">Mon – Fri: 8:00 AM – 4:00 PM</div>
                <div className="contact-info-sub">Saturday: 8:00 AM – 12:00 Noon (Closed Sundays & Holidays)</div>
              </div>
            </div>

            {/* Campus Address Card */}
            <div className="contact-info-card">
              <div className="contact-info-icon-pill">
                <i className="fa fa-map-marker-alt"></i>
              </div>
              <div>
                <div className="contact-info-label">Physical Location</div>
                <div className="contact-info-value">Vidyajyoti College of Theology</div>
                <div className="contact-info-sub">4A, Raj Niwas Marg, Ludlow Castle, Civil Lines, Delhi - 110054</div>
              </div>
            </div>

            {/* Email Contact Card */}
            <div className="contact-info-card">
              <div className="contact-info-icon-pill">
                <i className="fa fa-envelope"></i>
              </div>
              <div>
                <div className="contact-info-label">Official Correspondence</div>
                <div className="contact-info-value">vidyajyotilibrary@gmail.com</div>
                <div className="contact-info-sub">Reference & Digital Journal Inquiries: info@vjdelhi.org</div>
              </div>
            </div>

            {/* Phone Contact Card */}
            <div className="contact-info-card">
              <div className="contact-info-icon-pill">
                <i className="fa fa-phone-alt"></i>
              </div>
              <div>
                <div className="contact-info-label">Circulation Desk Telephone</div>
                <div className="contact-info-value">+91 92050 10207 / 98996 26793</div>
                <div className="contact-info-sub">Direct assistance during operational hours</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Modern Inquiry Form */}
        <div className="col-lg-7">
          <div className="contact-form-card">
            <div className="contact-form-title">Send Us a Library Inquiry</div>
            <div className="contact-form-desc">
              Fill in your details below and our library administration staff will get back to you promptly.
            </div>

            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6 form-group-modern">
                  <label className="form-label-modern">Full Name *</label>
                  <input
                    type="text"
                    className="form-input-modern"
                    placeholder="Enter full name"
                    value={contactDetails.username}
                    onChange={(e) => setContactDetails({ ...contactDetails, username: e.target.value })}
                  />
                </div>

                <div className="col-md-6 form-group-modern">
                  <label className="form-label-modern">Email Address *</label>
                  <input
                    type="email"
                    className="form-input-modern"
                    placeholder="name@vjdelhi.org"
                    value={contactDetails.emailID}
                    onChange={(e) => setContactDetails({ ...contactDetails, emailID: e.target.value })}
                  />
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6 form-group-modern">
                  <label className="form-label-modern">Phone Number *</label>
                  <input
                    type="text"
                    className="form-input-modern"
                    placeholder="+91 98765 43210"
                    value={contactDetails.phoneNumber}
                    onChange={(e) => setContactDetails({ ...contactDetails, phoneNumber: e.target.value })}
                  />
                </div>

                <div className="col-md-6 form-group-modern">
                  <label className="form-label-modern">Inquiry Type</label>
                  <select
                    className="form-select-modern"
                    value={contactDetails.inquiryType}
                    onChange={(e) => setContactDetails({ ...contactDetails, inquiryType: e.target.value })}
                  >
                    <option value="General Inquiry">General Library Inquiry</option>
                    <option value="Book Reservation">Book Reservation & Renewal</option>
                    <option value="Journal Request">Journal Article / Archive Request</option>
                    <option value="Inter-Library Loan">Inter-Library Research Access</option>
                    <option value="Book Recommendation">Recommend a New Book</option>
                  </select>
                </div>
              </div>

              <div className="form-group-modern">
                <label className="form-label-modern">Message / Specific Request *</label>
                <textarea
                  className="form-textarea-modern"
                  placeholder="Please describe your inquiry, book title, or research topic in detail..."
                  value={contactDetails.message}
                  onChange={(e) => setContactDetails({ ...contactDetails, message: e.target.value })}
                ></textarea>
              </div>

              <button type="submit" className="btn-contact-submit">
                <i className="fa fa-paper-plane"></i>
                <span>Submit Library Inquiry</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
